import { Controller } from "@hotwired/stimulus";
import {
  browserDeps,
  renderSupportFallback,
  openSupportChat,
  supportBody,
} from "../lib/supportHandoff.mjs";

// Per tab, like the conversation it keeps on screen: a new tab starts closed.
const OPEN_KEY = "kit-panel-open";

export default class extends Controller {
  static targets = ["drawer", "toggle", "badge", "supportStatus"];
  static values = {
    autoOpen: Boolean,
    autoOpenTrigger: Object,
    viewModeUrl: String,
  };

  connect() {
    this.isOpen = false;

    // Open on the last page means open on this one. Inertia visits keep the
    // panel mounted, but a full load (a link into a HAML page, a refresh) or a
    // Turbo visit renders it fresh; without this it came back closed mid-
    // conversation. It reappears where it was, so no open animation.
    if (sessionStorage.getItem(OPEN_KEY) === "true") this.openInstantly();

    // Listen for kit:open events from outside the controller scope (e.g. blank slate chips)
    this.handleKitOpen = (event) => {
      const message = event.detail && event.detail.message;
      if (message) {
        this.open();
        this.prefillWhenReady(message);
      } else {
        this.open();
      }
    };
    window.addEventListener("kit:open", this.handleKitOpen);

    // The mirror of kit:open, for anything mounted INSIDE the panel that needs
    // the panel out of the way — today that is the "Talk to a human" block
    // (chat_action_controller#support), which hands the corner to Intercom's
    // messenger exactly as the header's Support button does. A dispatched event
    // rather than a cross-controller reach: the panel owns whether it is open.
    this.handleKitClose = () => this.close();
    window.addEventListener("kit:close", this.handleKitClose);

    // First-visit auto-open (existing behavior — localStorage-based)
    if (this.autoOpenValue && !this.hasBeenDismissed()) {
      setTimeout(() => this.open(), 1500);
      return;
    }

    // They clicked a CTA in one of Kit's emails. Opens straight away and
    // regardless of a dismissal earlier in the session: every other trigger
    // here is Kit deciding to speak, and this one is the customer answering.
    // The 2s delay below is there to let a page settle before Kit interrupts
    // it — there is nothing to interrupt when the click is what brought them.
    if (
      this.hasAutoOpenTriggerValue &&
      this.autoOpenTriggerValue &&
      this.autoOpenTriggerValue.immediate
    ) {
      this.open();
      return;
    }

    // Smart trigger auto-open from server-rendered data attribute
    if (
      this.hasAutoOpenTriggerValue &&
      this.autoOpenTriggerValue &&
      this.autoOpenTriggerValue.name &&
      !this.hasDismissedThisSession()
    ) {
      setTimeout(() => {
        this.open();
        // Proactive CSM nudge: badge the launcher and mark the message seen so
        // the server-side trigger stops re-firing on the next navigation. The
        // nudge is already persisted in the chat, so it renders on load.
        if (this.autoOpenTriggerValue.name === "csm_outreach_pending") {
          this.showBadge();
          this.markCsmOutreachSeen();
          return;
        }
        // Proactive triggers create server-side messages with action buttons,
        // so no client-side prefill needed. Only prefill for non-proactive triggers.
        if (
          !this.autoOpenTriggerValue.proactive &&
          this.autoOpenTriggerValue.message
        ) {
          this.prefillWhenReady(this.autoOpenTriggerValue.message);
        }
      }, 2000);
    }
  }

  disconnect() {
    window.removeEventListener("kit:open", this.handleKitOpen);
    window.removeEventListener("kit:close", this.handleKitClose);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
      // The customer asked for Kit, so the composer is where their typing
      // goes. Only here: a restored or auto-opened panel must not take focus.
      const input = this.element.querySelector(
        "textarea[name='message[content]']"
      );
      if (input) input.focus({ preventScroll: true });
    }
  }

  open() {
    this.isOpen = true;
    this.element.classList.add("kit-chat-panel--open");
    sessionStorage.setItem(OPEN_KEY, "true");
    this.hideBadge();
    this.element.dispatchEvent(
      new CustomEvent("kit:opened", { bubbles: true })
    );
  }

  close() {
    this.isOpen = false;
    this.element.classList.remove("kit-chat-panel--open");
    sessionStorage.removeItem(OPEN_KEY);
    localStorage.setItem("kit-chat-dismissed", "true");
    sessionStorage.setItem("kit-dismissed-this-session", "true");
  }

  // `--restoring` switches the drawer's transitions off for the first paint;
  // two frames later it's gone, so closing still animates.
  openInstantly() {
    this.element.classList.add("kit-chat-panel--restoring");
    this.open();
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        this.element.classList.remove("kit-chat-panel--restoring")
      )
    );
  }

  // The header's Support button. Same promise, same code path and same
  // verification as Kit's in-conversation "Talk to a human" block
  // (chat_action_controller#support) — see lib/supportHandoff.mjs for why they
  // are one function now.
  //
  // Pre-fill a friendly opener so the composer isn't blank and support has
  // context, rather than dropping the user into an empty box.
  async openSupport() {
    if (this.supportOpening) return;
    this.supportOpening = true;
    if (this.hasSupportStatusTarget) this.supportStatusTarget.hidden = true;
    this.close();

    const body = supportBody(
      "Hi — I'd like some help from the team.",
      window.location.href
    );
    const result = await openSupportChat(body, browserDeps(window, document));
    this.supportOpening = false;
    if (result.ok) return;

    this.open();
    if (this.hasSupportStatusTarget)
      renderSupportFallback(this.supportStatusTarget, body);
  }

  // Expand to fill the window, or drop back to the corner drawer — and remember
  // which, because "make this my default" as a separate control would be a
  // second decision carrying no information the first one didn't.
  //
  // The class goes on the panel element (outside the turbo-frame) so a frame
  // reload can't drop it mid-conversation; the server re-renders it from
  // User#kit_view_mode on the next full page load.
  toggleExpand() {
    const expanded = this.element.classList.toggle("kit-chat-panel--expanded");
    this.persistViewMode(expanded ? "expanded" : "panel");
  }

  persistViewMode(mode) {
    if (!this.hasViewModeUrlValue) return;

    const meta = document.querySelector("meta[name='csrf-token']");
    fetch(this.viewModeUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": meta && meta.content,
        "Content-Type": "application/json",
        Accept: "text/html",
      },
      body: JSON.stringify({ mode }),
      // Best-effort: failing to remember the preference must never stop the
      // panel from resizing, which already happened above.
    }).catch(() => {});
  }

  clearChat() {
    const frame = document.querySelector("turbo-frame#brand_chat");
    // Clear the conversation the panel is actually showing. Before multi-chat
    // there was only one, so posting bare was the same thing; now it would
    // silently reset the shared thread while you're looking at a private one.
    const src = (frame && frame.getAttribute("src")) || "/kit";
    const chatId = new URL(src, window.location.origin).searchParams.get(
      "chat_id"
    );

    const meta = document.querySelector("meta[name='csrf-token']");
    const csrfToken = meta && meta.content;
    const clearUrl = chatId
      ? `/kit/clear?chat_id=${encodeURIComponent(chatId)}`
      : "/kit/clear";

    fetch(clearUrl, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html, text/html",
      },
    }).then((response) => {
      if (!response.ok || !frame) return;

      // A cleared shared thread is destroyed and recreated by #show; the id in
      // the URL is dead and KitController falls back to the new one. A private
      // thread survives with its messages gone and reopens on the same id.
      const next = chatId
        ? `/kit?chat_id=${encodeURIComponent(chatId)}`
        : "/kit";
      if (frame.getAttribute("src") === next) {
        frame.reload();
      } else {
        frame.src = next;
      }
    });
  }

  openWithMessage(event) {
    const message = event.params.message;
    this.open();

    // Wait for turbo frame to load, then pre-fill the input
    if (message) {
      this.prefillWhenReady(message);
    }
  }

  prefillWhenReady(message, attempts = 0) {
    if (attempts > 20) return;

    const form = this.element.querySelector("form[id^='new_message_']");
    if (!form) {
      setTimeout(() => this.prefillWhenReady(message, attempts + 1), 250);
      return;
    }

    const input =
      form.querySelector("textarea[name='message[content]']") ||
      form.querySelector("input[name='message[content]']");
    if (!input) {
      setTimeout(() => this.prefillWhenReady(message, attempts + 1), 250);
      return;
    }

    input.value = message;
    input.focus();
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }

  // Flip the pending CSM nudge to seen (server-side) so it doesn't re-open the
  // panel on every subsequent page. Best-effort: a failure just means the nudge
  // may surface again next navigation, which is acceptable.
  markCsmOutreachSeen() {
    const meta = document.querySelector("meta[name='csrf-token']");
    const csrfToken = meta && meta.content;
    fetch("/kit/mark_csm_seen", {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/html",
      },
    }).catch(() => {});
  }

  hasBeenDismissed() {
    return localStorage.getItem("kit-chat-dismissed") === "true";
  }

  hasDismissedThisSession() {
    return sessionStorage.getItem("kit-dismissed-this-session") === "true";
  }

  showBadge() {
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.add("kit-chat-badge--visible");
    }
  }

  hideBadge() {
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.remove("kit-chat-badge--visible");
    }
  }
}
